import { useMemo, useEffect, useRef, useState } from 'react';
import { getSponsorBucket, getRowDate } from './helpers';

/**
 * Pre-process raw data rows into enriched rows with _dur, _bucket, _court, _date.
 */
export function useProcessedRows(data) {
  return useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map(row => {
      const dur = parseFloat(row.Duration_Seconds) || parseFloat(row['On Screen Duration']) || 0;
      return {
        ...row,
        _dur: dur,
        _bucket: getSponsorBucket(row),
        _court: row['Court Name'] || row.Court || row.CourtName || 'Unknown',
        _date: getRowDate(row),
      };
    }).filter(r => r._dur > 0 && r._dur <= 60);
  }, [data]);
}

/**
 * Generate day list from tournament calendar or data.
 */
export function useUniqueDays(tournament, rows) {
  return useMemo(() => {
    if (tournament?.startDate && tournament?.endDate) {
      const days = [];
      const start = new Date(tournament.startDate);
      const end = new Date(tournament.endDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        days.push(d.toISOString().slice(0, 10));
      }
      if (days.length > 0) return days;
    }
    return [...new Set(rows.map(r => r._date).filter(Boolean))].sort();
  }, [tournament, rows]);
}

/**
 * Compute all metrics for the selected day + optional court filter.
 * Returns { computed, isComputing }.
 */
export function useComputeMetrics(rows, selectedDay, selectedCourt, uniqueDays) {
  const [computed, setComputed] = useState(null);
  const [isComputing, setIsComputing] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!selectedDay || rows.length === 0) return;
    setIsComputing(true);
    setComputed(null);
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      const dayRows = rows.filter(r => r._date === selectedDay);

      if (dayRows.length === 0) {
        setComputed({ empty: true, dayIndex: uniqueDays.indexOf(selectedDay) + 1, tournamentName: '' });
        setIsComputing(false);
        return;
      }

      const tournamentName = dayRows[0].Tournament || '';
      const dayIndex = uniqueDays.indexOf(selectedDay) + 1;
      const courts = [...new Set(dayRows.map(r => r._court).filter(c => c && c !== 'Unknown' && c !== ''))].sort();
      const filteredRows = selectedCourt ? dayRows.filter(r => r._court === selectedCourt) : dayRows;

      // Sponsor metrics
      const sponsorMetrics = {};
      filteredRows.forEach(r => {
        const b = r._bucket;
        if (!b) return;
        if (!sponsorMetrics[b]) sponsorMetrics[b] = { count: 0, duration: 0 };
        sponsorMetrics[b].count += 1;
        sponsorMetrics[b].duration += r._dur;
      });

      const buckets = Object.entries(sponsorMetrics)
        .sort(([, a], [, b]) => b.duration - a.duration)
        .map(([name]) => name);

      // Court breakdown (always full dayRows)
      const courtBreakdown = {};
      dayRows.forEach(r => {
        const b = r._bucket;
        const c = r._court;
        if (!b) return;
        if (!courtBreakdown[b]) courtBreakdown[b] = {};
        courtBreakdown[b][c] = (courtBreakdown[b][c] || 0) + 1;
      });

      // Last 60 minutes approximation
      const tail = Math.max(1, Math.ceil(dayRows.length * 0.15));
      const last60Rows = dayRows.slice(-tail);
      const courtBreakdownLast60 = {};
      last60Rows.forEach(r => {
        const b = r._bucket;
        const c = r._court;
        if (!b) return;
        if (!courtBreakdownLast60[b]) courtBreakdownLast60[b] = {};
        courtBreakdownLast60[b][c] = (courtBreakdownLast60[b][c] || 0) + 1;
      });

      // Hourly breakdown per sponsor
      const hourlyBreakdown = {};
      const parseTs = (ts) => {
        if (!ts) return null;
        const d = new Date(ts.$date || ts);
        return isNaN(d) ? null : d;
      };
      filteredRows.forEach(r => {
        const b = r._bucket;
        if (!b) return;
        const start = parseTs(r.StartTimestamp);
        if (!start) return;
        const hour = start.getHours();
        const label = `${String(hour).padStart(2, '0')}:00-${String(hour + 1).padStart(2, '0')}:00`;
        if (!hourlyBreakdown[b]) hourlyBreakdown[b] = {};
        hourlyBreakdown[b][label] = (hourlyBreakdown[b][label] || 0) + 1;
      });

      // T = total match time in minutes
      const courtSpans = {};
      filteredRows.forEach(r => {
        const c = r._court;
        const parseTsNum = (ts) => {
          if (!ts) return NaN;
          return new Date(ts.$date || ts).getTime();
        };
        const s = parseTsNum(r.StartTimestamp);
        const e = parseTsNum(r.EndTimestamp);
        if (!isNaN(s) && !isNaN(e)) {
          if (!courtSpans[c]) courtSpans[c] = { min: s, max: e };
          if (s < courtSpans[c].min) courtSpans[c].min = s;
          if (e > courtSpans[c].max) courtSpans[c].max = e;
        }
      });
      const totalMatchMs = Object.values(courtSpans).reduce((sum, { min, max }) => sum + (max - min), 0);
      const totalMatchMins = totalMatchMs > 0
        ? Math.max(totalMatchMs / 60000, 1)
        : Math.max(filteredRows.reduce((s, r) => s + r._dur, 0) / 60, 1);

      setComputed({
        dayRows, sponsorMetrics, buckets,
        courtBreakdown, courtBreakdownLast60, hourlyBreakdown,
        tournamentName, dayIndex, courts, totalMatchMins,
      });
      setIsComputing(false);
    }, 0);

    return () => clearTimeout(timerRef.current);
  }, [rows, selectedDay, selectedCourt, uniqueDays]);

  return { computed, isComputing };
}
